import { useState } from 'react';
import {
  AppBar, Toolbar, Typography, Box, Button, Container, Stack, Divider, Link,
  IconButton, Menu, MenuItem, useMediaQuery, useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import LoginModal from './LoginModal';

// Shared shell for all public-facing pages: a white header (matching the
// official Taraba State site) with the logo and green accents, and a light
// footer with green accents. The Sign in modal is wired in.
export default function PublicLayout({ children, maxWidth = 'lg' }) {
  const navigate = useNavigate();
  const [loginOpen, setLoginOpen] = useState(false);
  const [navAnchor, setNavAnchor] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* White header with a subtle bottom border */}
      <AppBar
        position="sticky"
        sx={{ bgcolor: '#FFFFFF', borderBottom: '1px solid #E4E8E4' }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, minWidth: 0, cursor: 'pointer' }}
            onClick={() => navigate('/portal')}
          >
            <Box component="img" src="/logo.png" alt="Taraba State" sx={{ height: 40, width: 40, flexShrink: 0 }} />
            <Typography
              variant="h6"
              noWrap
              sx={{ color: 'text.primary', fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              {isMobile ? 'Procurement Portal' : 'Taraba State Procurement Portal'}
            </Typography>
          </Box>

          {/* Register stays visible at every width -- it is the action the
              State most wants from a visitor, so it does not go in a menu. */}
          <Button
            variant="contained"
            color="primary"
            sx={{ flexShrink: 0 }}
            onClick={() => navigate('/register')}
          >
            {isMobile ? 'Register' : 'Register as vendor'}
          </Button>

          {isMobile ? (
            <>
              <IconButton color="primary" onClick={(e) => setNavAnchor(e.currentTarget)} aria-label="Open menu">
                <MenuIcon />
              </IconButton>
              <Menu anchorEl={navAnchor} open={!!navAnchor} onClose={() => setNavAnchor(null)}>
                <MenuItem onClick={() => { setNavAnchor(null); navigate('/portal'); }}>
                  Tenders
                </MenuItem>
                {/* Deferred: opening the modal while the menu is still
                    unmounting drops the state update. */}
                <MenuItem onClick={() => { setNavAnchor(null); setTimeout(() => setLoginOpen(true), 0); }}>
                  Sign in
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Button color="primary" onClick={() => navigate('/portal')}>Tenders</Button>
              <Button color="primary" onClick={() => setLoginOpen(true)}>Sign in</Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth={maxWidth} sx={{ py: 4, flexGrow: 1, width: '100%' }}>
        {children}
      </Container>

      {/* Light footer with green accents */}
      <Box
        component="footer"
        sx={{ bgcolor: '#FFFFFF', borderTop: '2px solid', borderColor: 'primary.main', mt: 'auto' }}
      >
        <Container maxWidth={maxWidth} sx={{ py: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box component="img" src="/logo.png" alt="Taraba State" sx={{ height: 36, width: 36 }} />
              <Box>
                <Typography variant="subtitle2" color="primary">Taraba State E-Procurement Portal</Typography>
                <Typography variant="caption" color="text.secondary">Transparent, competitive public procurement.</Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Helpdesk: support@taraba-eproc.gov.ng</Typography>
              <Typography variant="caption" color="text.secondary" display="block">+234 903 069 0129</Typography>
            </Box>
            <Box>
              <Link component={RouterLink} to="/portal" color="primary" variant="caption" display="block" underline="hover">Public tenders</Link>
              <Link component={RouterLink} to="/open-data/publication-policy" color="primary" variant="caption" display="block" underline="hover">Open data</Link>
              <Link component="button" color="primary" variant="caption" underline="hover" onClick={() => setLoginOpen(true)}>Sign in</Link>
              <Typography variant="caption" color="text.secondary" display="block">Privacy notice</Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">© {new Date().getFullYear()} Taraba State Government. All rights reserved.</Typography>
        </Container>
      </Box>

      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </Box>
  );
}